import { useOutletContext, useRouteLoaderData } from '@remix-run/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import type { OutletContext } from '~/interfaces/outlet_context';

import type { PostLike } from '~/hooks/useLoaders/usePostInsights';

import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import useGlobalModals from '../GlobalModals/hooks/useGlobalModals';
import { getIsLiked } from './getIsLiked';

import type { RootLoaderData } from '~/root';

type RenderProps = {
  isLoading: boolean;
  isLiked: boolean;
  likesCount: number;
};

type Props = {
  children: (props: RenderProps) => ReactNode;
  postId: string;
  likes: PostLike[];
  areLikesFetched: boolean;
  onSuccess?: () => void;
};

const PostLikeAction = ({ children, likes, areLikesFetched, postId, onSuccess }: Props) => {
  const fetcher = useComponentFetcher();
  const isLoading = fetcher.state === 'submitting';

  const formRef = useRef<HTMLFormElement>(null);

  const { handleOpenModal } = useGlobalModals();

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);

  const { admin } = useOutletContext<OutletContext>();
  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const isAdminLoggedIn = Boolean(admin);
  const isSubcriberLoggedIn = Boolean(subscription);
  const isNotLoggedIn = !isAdminLoggedIn && !isSubcriberLoggedIn;

  useEffect(() => {
    if (areLikesFetched && !hasMounted) {
      setLiked(getIsLiked(likes, admin, subscription));
      setLikeCount(likes.filter((like) => like.liked === true).length);
      setHasMounted(true);
    }
  }, [areLikesFetched, hasMounted]);

  const updateAttributionInState = () => {
    if (liked) {
      setLiked(false);
      setLikeCount(likeCount - 1);
    } else {
      setLiked(true);
      setLikeCount(likeCount + 1);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isNotLoggedIn) {
      handleOpenModal('login');
      return;
    }

    const formData = new FormData(e.currentTarget);

    if (isAdminLoggedIn) {
      fetcher.submit(formData, {
        method: 'post',
        action: '/admins/post_like',
      });
    } else {
      fetcher.submit(formData, { method: 'post', action: '/post_like' });
    }
  };

  return (
    <ActionForm
      fetcher={fetcher}
      ref={formRef}
      method="post"
      action={isAdminLoggedIn ? '/admins/post_like' : '/post_like'}
      onSubmit={handleSubmit}
      onSuccess={() => {
        updateAttributionInState();
        onSuccess?.();
      }}
      onError={(data) => {
        toast.error(data?.toast?.message || 'Something went wrong, please try again');
      }}
    >
      <input type="hidden" name="post_id" value={postId} />
      <input type="hidden" name="subscription_id" value={subscription?.id} />
      <input type="hidden" name="user_id" value={admin?.id} />
      {children({ isLoading, isLiked: liked, likesCount: likeCount })}
    </ActionForm>
  );
};

export default PostLikeAction;
